/** ******************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2021, 2025
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} * */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from 'contexts/ApiHandler';
import JavaKafkaCodeSnippet from 'code-snippets/KafkaSnippets/JavaKafkaCodeSnippet';

const propertyKeys = {
  keyDeserializer: 'key.deserializer',
  valueDeserializer: 'value.deserializer',
  bootstrapServers: 'bootstrap.servers',
  clientId: 'client.id',
  saslMechanism: 'sasl.mechanism',
  securityProtocol: 'security.protocol'
};

const useKafkaProperties = (channelName, operationId) => {
  const [properties, updateProperties] = useState([]);
  const { t } = useTranslation();
  const { apiHandler } = useApi();
  // Java code snippet properties are the same as kafka properties so re-use that here
  const parser = useMemo(() => new JavaKafkaCodeSnippet(apiHandler, channelName, {}, t, undefined, operationId), [apiHandler, channelName, operationId, t]);
  const getPropertyForKey = useCallback(
    (key) => {
      switch (key) {
        case propertyKeys.keyDeserializer:
          return {
            key,
            value: parser.getKeyDeserializer().deserializerClass
          };
        case propertyKeys.valueDeserializer:
          return {
            key,
            value: parser.getSchemaInformation().deserializerClass
          };
        case propertyKeys.bootstrapServers:
          return {
            key,
            value: parser.getBootstrapServers()
          };
        case propertyKeys.clientId:
          return {
            key,
            value: parser.getOperationBindings().clientId
          };
        case propertyKeys.saslMechanism:
          return {
            key,
            value: parser.getSecurityDetails().saslMechanism
          };
        case propertyKeys.securityProtocol:
          return {
            key,
            value: parser.getSecurityDetails().securityProtocol
          };
        default:
          break;
      }
    },
    [parser]
  );

  useEffect(() => {
    const properties = Object.values(propertyKeys)
      .map((propertyKey) => getPropertyForKey(propertyKey))
      .filter((property) => !!property.value); // remove any properties that we couldn't work out the value for
    updateProperties(properties);
  }, [channelName, getPropertyForKey]);

  return properties;
};

export default useKafkaProperties;
